import React, { useState, useEffect } from 'react';
import { Box , Typography, Table, TableBody, TextField,InputAdornment,
  TableContainer, Chip, TableHead,CircularProgress, TableRow, 
  Paper,IconButton } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { grey, deepPurple, red,orange,blue,green } from '@mui/material/colors';
import Nuevoreporte from './Nuevoreporte';
import Deletereporte from './Deletereporte';  
import Editarreporte from './Editarreporte'; 
import BASE_URL from '../../config';

import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import EditIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import NewIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: grey[100],
    color: theme.palette.common.black,
  }
}));

export default function ReportesPage() {
  // Usuario y permisos: con solo "Ver Reportes" la pantalla es de solo
  // lectura y únicamente muestra los reportes propios (como encargado o agente).
  const usuarioActual = (() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
  })();
  const permisos = usuarioActual?.permisos || [];
  const puedeGestionar = permisos.includes('Gestionar reportes');

  const [reporteSeleccionado, setReporteSeleccionado] = useState(null)
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  // IDs de agendas propias (solo se usan cuando no puede gestionar).
  const [agendasPropias, setAgendasPropias] = useState(null);

  const fetchReportes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/reportes/`);
      if (!res.ok) throw new Error("Error en la red");
      const data = await res.json();
      const listaFinal = Array.isArray(data) ? data : (data.data || []);

      setReportes(listaFinal);
    } catch (e) {
      console.error("Error:", e);
      setReportes([]);
    } finally {
      setLoading(false);
    }
  };

  // Para usuarios de solo lectura: traer las agendas y quedarnos con los
  // ids de las que son suyas (encargado o agente), para filtrar reportes.
  const fetchAgendasPropias = async () => {
    try {
      const res = await fetch(`${BASE_URL}/agendas/`);
      if (!res.ok) throw new Error("Error en la red");
      const data = await res.json();
      const lista = Array.isArray(data) ? data : (data.data || []);
      const ids = lista
        .filter(a =>
          a.usuario_id === usuarioActual.id ||
          (Array.isArray(a.lista_agentes_ids) &&
            a.lista_agentes_ids.includes(usuarioActual.id)))
        .map(a => a.id);
      setAgendasPropias(new Set(ids));
    } catch (e) {
      console.error("Error:", e);
      setAgendasPropias(new Set());
    }
  };

  // Abre el PDF del reporte (solo lectura) en una pestaña nueva.
  const verPdf = async (reporteId) => {
    try {
      const res = await fetch(`${BASE_URL}/reportes/${reporteId}/preview?t=${Date.now()}`);
      if (!res.ok) throw new Error("Error al obtener el PDF");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      console.error("Error al ver PDF:", e);
    }
  };

  useEffect(() => {
    fetchReportes();
    if (!puedeGestionar) fetchAgendasPropias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportesFiltrados = reportes.filter((item) => {
    const coincideBusqueda =
      item.nombre_cliente?.toLowerCase().includes(busqueda.toLowerCase());
    if (puedeGestionar) return coincideBusqueda;
    // Solo lectura: además, que sea un reporte de una agenda propia.
    const esPropia = agendasPropias?.has(item.agenda_id);
    return coincideBusqueda && esPropia;
  });

  const getEstadoStyle = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'enviado': return { color: green[700], bg: green[50] };
      case 'pendiente': return { color: orange[700], bg: orange[50] };
      case 'no realizado': return { color: red[700], bg: red[50] };
      default: return { color: grey[700], bg: grey[100] };
    }
  };

  
  const getPrioridadStyle = (prioridad) => {
    switch (prioridad?.toLowerCase()) {
    case'baja':return { color: green[700], bg:green[50] };
    case'media':return { color: blue[700], bg: blue[50] };
    case'alta':return { color: orange[700], bg: orange[50]};  
    case'inmediata':return { color: red[700], bg:red[50] };
    default:return{ color: blue[700], bg:blue[50] };
    }
  };

  if (reporteSeleccionado) {
    return (
      <Box sx={{ p: 2 }}>
        {reporteSeleccionado.estado_reporte === 'no realizado' ? (
          <Nuevoreporte 
            reporte={reporteSeleccionado} 
            alCerrar={() => {
              setReporteSeleccionado(null);
              fetchReportes();
            }} 
          />
        ) : (
          <Editarreporte 
            reporte={reporteSeleccionado} 
            alCerrar={() => {
              setReporteSeleccionado(null);
              fetchReportes();
            }} 
          />
        )}
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0, m: 0 }}>
      <Box sx={{display:'flex',flexDirection:{xs:'column',sm:'row'},alignItems:{xs:'flex-start',sm:'center',gap:2},justifyContent:'space-between'}}>
        <Box sx={{mr:2}}>
          <Typography variant='h6'>Administración de Reportes a Clientes</Typography>
          <Typography variant='body1' color='textSecondary'>Administra los reportes </Typography>
        </Box>
        <Box>
        {/* BUSCADOR DE CLIENTE */}
          <TextField
            size="small"
            placeholder="Buscar por cliente..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{ 
              width: { xs: '100%', sm: '300px' },
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } 
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

        <TableContainer component={Paper}  sx={{mt:3, maxHeight: 590,
            borderRadius: 3,
            border:`1px solid ${grey[400]}`,
            overflowX: 'auto' , '&::-webkit-scrollbar': {
            display: 'none',
            },
            // Ocultar para Firefox
            scrollbarWidth: 'none',
            // Ocultar para Internet Explorer y Edge antiguo
            msOverflowStyle: 'none',}}>

          <Table  sx={{minWidth: 200 }} stickyHeader aria-label="customized table" >
          <TableHead sx={{ bgcolor: grey[100] }}>
            <TableRow >
              <StyledTableCell sx={{fontWeight:'bold'}}>Empresa</StyledTableCell>
              <StyledTableCell sx={{fontWeight:'bold'}}>Encargado</StyledTableCell>
              <StyledTableCell sx={{fontWeight:'bold'}}>Estado</StyledTableCell>
              <StyledTableCell sx={{fontWeight:'bold'}}>Prioridad</StyledTableCell>
              <StyledTableCell sx={{fontWeight:'bold'}} align='right' >Acciones</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // ESTADO: CARGANDO
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                  <CircularProgress size={40} sx={{ color: grey[500] }} />
                  <Typography sx={{ mt: 2, color: grey[600] }}>Cargando reportes...</Typography>
                </TableCell>
              </TableRow>
              ) : reportesFiltrados.length > 0 ? (
              reportesFiltrados.map((item) => {
                const estadoStyle = getEstadoStyle(item.estado_reporte);
                const prioridadStyle = getPrioridadStyle(item.prioridad_reporte);
                return (
                  <TableRow  
                      hover 
                      key={item.id} 
                      sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      height: '60px' 
                    }}
                  >
                    {/* CLIENTE */}
                    <TableCell>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        lineHeight: 1,
                        gap: 1 
                      }}>
                        <StorefrontOutlinedIcon/>
                        <Typography variant="body2">
                          {item.nombre_cliente}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* ENCARGADO */}
                    <TableCell variant='body'>
                      {item.nombre_usuario || (
                        <span style={{ color: red[700], fontStyle: 'italic' }}>Sin definir</span>
                      )}
                    </TableCell>

                    {/* ESTADO */}
                    <TableCell variant='body'>
                      <Chip 
                        label={item.estado_reporte} 
                        size="small"
                        sx={{ 
                          bgcolor: estadoStyle.bg, 
                          color: estadoStyle.color, 
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          border: `1px solid ${estadoStyle.color}40`
                        }} 
                      />
                    </TableCell>

                    {/* PRIORIDAD */}
                    <TableCell variant='body'>
                      <Chip 
                        label={item.prioridad_reporte} 
                        size="small"
                        sx={{ 
                          bgcolor: prioridadStyle.bg, 
                          color: prioridadStyle.color, 
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          textTransform: 'uppercase',
                          border: `1px solid ${prioridadStyle.color}40`
                        }} 
                      />
                    </TableCell>

                    {/* ACCIONES */}
                    <TableCell align="right" sx={{display:'flex',flexDirection:'row', justifyContent:'end', gap:0.5}}>
                      {puedeGestionar ? (
                        <>
                          {item.estado_reporte === 'no realizado' ? (
                            /* BOTÓN PARA CREAR NUEVO */
                            <IconButton onClick={() => setReporteSeleccionado(item)} sx={{'&:hover': {color:grey[100], bgcolor: blue[800] } }}>
                              <NewIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            /* BOTÓN PARA EDITAR EXISTENTE */
                            <IconButton onClick={() => setReporteSeleccionado(item)} sx={{'&:hover': {color:grey[100], bgcolor: deepPurple[400] } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Deletereporte reporte={item} refresh={fetchReportes}/>
                        </>
                      ) : (
                        /* SOLO LECTURA: ver/descargar el PDF (si ya existe reporte) */
                        item.estado_reporte === 'no realizado' ? (
                          <Typography variant="caption" color="textSecondary">Sin reporte</Typography>
                        ) : (
                          <IconButton title="Ver PDF" onClick={() => verPdf(item.reporte_id)} sx={{'&:hover': {color:grey[100], bgcolor: blue[800] } }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )
                      )}
                    </TableCell>

                  </TableRow>
                );
              })
              ) : (
              // ESTADO
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 12 }}>
                  <Box sx={{ opacity: 0.4 }}>
                    <AssignmentLateIcon sx={{ fontSize: 70, color: grey[500] }} />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                      No hay reportes registrados
                    </Typography>
                    <Typography variant="body2">
                      Cuando se generen reportes desde la agenda, aparecerán en esta lista.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          </Table>
        </TableContainer>

    </Box>
  );
}